/** Base API client with auth token management. The API base and, for an app
 *  mounted inside a host page, the token source and 401 handling come from
 *  appConfig.ts; the defaults are this app's own /api/v1 and stored tokens. */

import { apiBase, getAppConfig } from '../appConfig.ts'

let authToken: string | null = localStorage.getItem('auth_token')
let refreshTokenValue: string | null = localStorage.getItem('refresh_token')

export function setAuthToken(token: string | null) {
  authToken = token
  if (token) {
    localStorage.setItem('auth_token', token)
  } else {
    localStorage.removeItem('auth_token')
  }
}

export function getAuthToken(): string | null {
  return authToken
}

export function setRefreshToken(token: string | null) {
  refreshTokenValue = token
  if (token) {
    localStorage.setItem('refresh_token', token)
  } else {
    localStorage.removeItem('refresh_token')
  }
}

export function getRefreshToken(): string | null {
  return refreshTokenValue
}

/** The token sent with each request: the host's (configureApp's getToken),
 *  read per request, else the stored one above. */
export function currentToken(): string | null {
  const { getToken } = getAppConfig()
  return getToken ? getToken() : authToken
}

/** Error thrown for a non-ok HTTP response. Carries the status + parsed detail
 *  so callers can branch on it (e.g. 409 optimistic-concurrency handling).
 *  `message` stays the server-provided `detail` so existing catch/`.message`
 *  callers are unaffected. */
export class ApiError extends Error {
  readonly status: number
  readonly detail: string

  constructor(status: number, detail: string) {
    super(detail)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }
}

/** Type guard for {@link ApiError}. */
export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError
}

/** True when `err` is a 409 (stale optimistic-concurrency write). */
export function isConflictError(err: unknown): err is ApiError {
  return isApiError(err) && err.status === 409
}

// 401 handler — set by AuthProvider to clear React state without hard navigation
let unauthorizedHandler: (() => void) | null = null

export function setUnauthorizedHandler(handler: () => void) {
  unauthorizedHandler = handler
}

// Single-flight refresh: concurrent 401s share one refresh call.
let refreshPromise: Promise<boolean> | null = null

/** Exchange the stored refresh token for a new access + refresh token pair.
 *  Returns true on success. Uses raw fetch so it cannot recurse into
 *  request()'s own 401 handling. */
async function tryRefreshTokens(): Promise<boolean> {
  if (!refreshTokenValue) return false
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const response = await fetch(`${apiBase()}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshTokenValue }),
        })
        if (!response.ok) return false
        const data = await response.json()
        if (!data.access_token) return false
        setAuthToken(data.access_token)
        if (data.refresh_token) setRefreshToken(data.refresh_token)
        return true
      } catch {
        return false
      } finally {
        // Allow future refreshes after this one settles.
        setTimeout(() => { refreshPromise = null }, 0)
      }
    })()
  }
  return refreshPromise
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  isRetry = false,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  }

  const token = currentToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${apiBase()}${path}`, {
    ...options,
    headers,
  })

  if (response.status === 401) {
    const { getToken, onUnauthorized } = getAppConfig()
    // A host that owns the session handles its expiry: nothing here
    // refreshes or clears it.
    if (onUnauthorized) {
      onUnauthorized()
      throw new Error('Unauthorized')
    }
    // Expired access token: silently refresh and retry once before logging out.
    // A host-supplied token has no stored refresh token to exchange.
    if (!isRetry && !getToken && (await tryRefreshTokens())) {
      return request<T>(path, options, true)
    }
    setAuthToken(null)
    setRefreshToken(null)
    if (unauthorizedHandler) {
      unauthorizedHandler()
    }
    throw new Error('Unauthorized')
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({ detail: response.statusText }))
    throw new ApiError(response.status, body.detail || `HTTP ${response.status}`)
  }

  if (response.status === 204) {
    return undefined as T
  }

  return response.json()
}

/** fetch() against the API for callers that need the raw Response (file
 *  downloads): the API base, the current token, and a 401 handed to the
 *  host's onUnauthorized when one is configured. Unlike request() it never
 *  refreshes or clears tokens; every status is left to the caller. */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = currentToken()
  const response = await fetch(`${apiBase()}${path}`, {
    ...init,
    headers: {
      ...(init.headers as Record<string, string> | undefined),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })
  if (response.status === 401) {
    getAppConfig().onUnauthorized?.()
  }
  return response
}

export function get<T>(path: string): Promise<T> {
  return request<T>(path)
}

export function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  })
}

export function patch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  })
}

export function put<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  })
}

export function del<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' })
}
